import { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { Language, getLanguage, setLanguage, initLanguage, loadTranslations } from '@/lib/translations';

type LanguageContextType = {
  currentLanguage: Language;
  changeLanguage: (lang: Language) => void;
  translationsReady: boolean;
};

const LanguageContext = createContext<LanguageContextType>({
  currentLanguage: 'en',
  changeLanguage: () => {},
  translationsReady: false,
});

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [languageState, setLanguageState] = useState<Language>(getLanguage());
  const [translationsReady, setTranslationsReady] = useState(false);

  useEffect(() => {
    initLanguage();
    const lang = getLanguage();
    setLanguageState(lang);
    loadTranslations(lang).then(() => {
      setTranslationsReady(true);
    }).catch(() => {
      setTranslationsReady(true);
    });

    // First-visit geo detection: if the user has not explicitly chosen
    // a language yet, default to Danish for Danish visitors and English
    // for everyone else. We only set the preference once we have a
    // confident answer so the LanguageSelector still appears unchanged
    // for any user who later wants to switch manually.
    if (!localStorage.getItem('app-language')) {
      const controller = new AbortController();
      fetch('/api/geo', { signal: controller.signal })
        .then((r) => (r.ok ? r.json() : null))
        .then((data: { country: string | null } | null) => {
          if (!data || localStorage.getItem('app-language')) return;
          const detected: Language = data.country === 'DK' ? 'da' : 'en';
          setLanguage(detected);
          setLanguageState(detected);
          loadTranslations(detected)
            .then(() => setTranslationsReady(true))
            .catch(() => setTranslationsReady(true));
        })
        .catch(() => {
          /* network failure → fall back to whatever initLanguage chose */
        });
      return () => controller.abort();
    }
  }, []);

  const handleLanguageChange = (lang: Language) => {
    if (lang === languageState) return;
    setLanguage(lang);
    setLanguageState(lang);
  };

  useEffect(() => {
    document.documentElement.lang = languageState;
  }, [languageState]);

  useEffect(() => {
    const handleLanguageEvent = (e: CustomEvent<Language>) => {
      setLanguageState(e.detail);
      setTranslationsReady(true);
    };

    window.addEventListener('language-changed', handleLanguageEvent as EventListener);
    
    return () => {
      window.removeEventListener('language-changed', handleLanguageEvent as EventListener);
    };
  }, []);

  const contextValue: LanguageContextType = {
    currentLanguage: languageState,
    changeLanguage: handleLanguageChange,
    translationsReady,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  
  return context;
}
